import React, { useState, useEffect, useCallback, useRef } from 'react'
import { ConfigJsonToggle } from './ConfigJsonToggle'
import { ChainVisualEditor } from './ChainVisualEditor'

/**
 * Orchestration component for the chain visual editor.
 *
 * Bridges React <-> formlib DOM (same pattern as SimulationEditor):
 *   - On mount: parse `steps` textarea -> React state, hide formlib row, parse simulations data
 *   - On every state change: JSON.stringify(steps) -> textarea
 *   - JSON mode toggle: show/hide formlib row, re-parse on switch back
 *   - Renders ChainVisualEditor when not in JSON mode
 *
 * Props:
 *   stepsTextareaId    — DOM id of the formlib steps textarea
 *   simulationsDataId  — DOM id of the <script type="application/json"> data block
 *   coursenum           — current course number
 */
export const ChainEditor = ({ stepsTextareaId, simulationsDataId, coursenum }) => {
  const [steps, setSteps] = useState([])
  const [simulations, setSimulations] = useState([])
  const [jsonMode, setJsonMode] = useState(false)
  const [parseError, setParseError] = useState(null)
  const stepsRowRef = useRef(null)

  // Read initial state from DOM on mount
  useEffect(() => {
    const stepsTextarea = document.getElementById(stepsTextareaId)
    if (stepsTextarea) {
      try {
        const raw = stepsTextarea.value.trim()
        setSteps(raw ? JSON.parse(raw) : [])
      } catch (e) {
        setSteps([])
      }
      // Find and hide the formlib row
      const row = stepsTextarea.closest('[data-name="steps"]')
      if (row) {
        stepsRowRef.current = row
        row.style.display = 'none'
      }
    }

    // Parse available simulations from the data block
    const dataEl = document.getElementById(simulationsDataId)
    if (dataEl) {
      try {
        const parsed = JSON.parse(dataEl.textContent)
        setSimulations(Array.isArray(parsed) ? parsed : [])
      } catch (e) {
        setSimulations([])
      }
    }
  }, [stepsTextareaId, simulationsDataId])

  // Sync steps back to textarea on every change
  useEffect(() => {
    const stepsTextarea = document.getElementById(stepsTextareaId)
    if (stepsTextarea) {
      stepsTextarea.value = JSON.stringify(steps, null, 2)
    }
  }, [steps, stepsTextareaId])

  // Show/hide formlib row based on JSON mode
  useEffect(() => {
    if (stepsRowRef.current) {
      stepsRowRef.current.style.display = jsonMode ? '' : 'none'
    }
  }, [jsonMode])

  const handleChange = useCallback((newSteps) => {
    setSteps(newSteps)
  }, [])

  const handleJsonToggle = useCallback((wantJson) => {
    if (wantJson) {
      setJsonMode(true)
      setParseError(null)
    } else {
      // Switching from JSON to visual — re-parse textarea
      const stepsTextarea = document.getElementById(stepsTextareaId)
      try {
        const raw = stepsTextarea ? stepsTextarea.value.trim() : ''
        const parsed = raw ? JSON.parse(raw) : []
        if (raw && !Array.isArray(parsed)) {
          throw new Error('Steps must be a JSON array')
        }
        setSteps(parsed)
        setJsonMode(false)
        setParseError(null)
      } catch (e) {
        setParseError(`Cannot switch to visual mode: ${e.message}. Fix the JSON and try again.`)
      }
    }
  }, [stepsTextareaId])

  return (
    <div style={{ marginTop: '12px' }}>
      <ConfigJsonToggle
        jsonMode={jsonMode}
        onToggle={handleJsonToggle}
        hasVisualEditor={true}
        parseError={parseError}
      />
      {!jsonMode && (
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '16px',
          background: '#fff',
        }}>
          <ChainVisualEditor
            steps={steps}
            simulations={simulations}
            coursenum={coursenum}
            onChange={handleChange}
          />
        </div>
      )}
    </div>
  )
}
